import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { InfoIcon } from '@phosphor-icons/react/Info';
import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { MatchingOperator } from '../../../../../../../modules/audiences/AudienceForm/constants.ts';
import type {
  AudienceForm,
  AudienceListType,
  Group,
} from '../../../../../../../modules/audiences/AudienceForm/types.ts';
import type { EnforceMarketUnionOperatorsCallOptions } from '../../../../../../../modules/audiences/AudienceForm/utils/applyMarketForcedUnionOperators.ts';
import type { MarketOperatorRestrictionReason } from '../../../../utils/marketOperatorRestrictions.ts';
import { ConfirmationModal } from '../../../ConfirmationModal.tsx';
import { audienceLocation, type SelectedLocationSegment } from '../../constants.ts';
import type { DrawerState } from '../../drawerTypes.ts';
import {
  generateInitialSelectedLocationState,
  type SelectedLocationInitialState,
} from '../utils/generateInitialSelectedLocationState.ts';
import { type CriteriaGroupFieldName, useSubGroupHandlers } from './hooks/useSubGroupHandlers.ts';
import { SubGroupItem } from './SubGroupItem.tsx';
export type SubGroupListProps = {
  fieldPath: CriteriaGroupFieldName;
  parentIndex: number;
  listType: AudienceListType;
  removeSubgroup: (lastSubgroup?: Group) => void;
  setSubgroupOperator: (operator: MatchingOperator) => void;
  setDrawerState: Dispatch<SetStateAction<DrawerState>>;
  isEdit: boolean;
  shouldUseRegionFirstFlow: boolean;
  siblingOperatorRestrictionReason: MarketOperatorRestrictionReason | null;
  errorMessage?: string;
  isExpanded: (id?: string) => boolean;
  toggle: (id?: string) => () => void;
  setExpanded: (id: string | undefined, expanded: boolean) => void;
  onAfterSubgroupTreeMutation?: (options?: EnforceMarketUnionOperatorsCallOptions) => void;
};

export const SubGroupList = ({
  fieldPath,
  parentIndex,
  removeSubgroup,
  setSubgroupOperator,
  listType,
  setDrawerState,
  isEdit,
  shouldUseRegionFirstFlow,
  siblingOperatorRestrictionReason,
  errorMessage,
  isExpanded,
  toggle,
  setExpanded,
  onAfterSubgroupTreeMutation,
}: SubGroupListProps) => {
  const { t } = useTranslation();
  const runAfterMutation =
    onAfterSubgroupTreeMutation ?? ((_options?: EnforceMarketUnionOperatorsCallOptions) => undefined);
  const { setValue } = useFormContext<AudienceForm>();
  const fieldName = `${fieldPath}.${parentIndex}` as const;
  const fieldArrayPath = `${fieldPath}.${parentIndex}.groups` as const;

  const {
    fields: subgroupFields,
    append,
    remove,
    update,
  } = useFieldArray<AudienceForm, typeof fieldArrayPath>({
    name: fieldArrayPath,
  });

  const initialSelectedLocationState = useMemo(
    () => generateInitialSelectedLocationState(subgroupFields),
    [subgroupFields]
  );

  const [selectedLocationSegment, setSelectedLocationSegment] =
    useState<SelectedLocationInitialState>(initialSelectedLocationState);

  const handleSelectedLocationSegmentChange = useCallback(
    (segment: SelectedLocationSegment, index: number | string) => {
      setSelectedLocationSegment(prev => ({
        ...prev,
        [index]: segment,
      }));
    },
    []
  );

  const typedUpdate = useCallback(
    (index: number, value: Group) => {
      update(index, value);
      runAfterMutation({ includeSellableMultipartner: true });
    },
    [update, runAfterMutation]
  );

  const { selectedIndex, handleToggleOperator, handleCloseModal, handleConfirmDelete, handleRemoveSubgroup } =
    useSubGroupHandlers({
      fieldPath,
      fields: subgroupFields,
      remove,
      removeSubgroup,
      setSubgroupOperator,
      parentIndex,
      onAfterMutation: runAfterMutation,
    });

  const handleAddSubgroup = useCallback(() => {
    append({ uuid: uuid(), operator: MatchingOperator.INTERSECTION });

    if (subgroupFields.length === 1) {
      setValue(`${fieldName}.operator`, MatchingOperator.INTERSECTION);
    }

    runAfterMutation();
  }, [append, subgroupFields.length, fieldName, setValue, runAfterMutation]);

  return (
    <Box data-test="subgroup-container">
      <ConfirmationModal
        isOpen={selectedIndex !== null}
        onClose={handleCloseModal}
        onDelete={handleConfirmDelete}
        title={t('Remove Subgroup')}
        item={
          selectedIndex !== null
            ? t('Subgroup {parentIndex}.{childCount}', {
                parentIndex: parentIndex + 1,
                childCount: selectedIndex + 1,
              })
            : ''
        }
      />

      {subgroupFields.map((field, index) => {
        const isLastItem = index + 1 === subgroupFields.length;

        return (
          <SubGroupItem
            key={field.id}
            field={field}
            index={index}
            isEdit={isEdit}
            fieldName={fieldName}
            parentIndex={parentIndex}
            isLastItem={isLastItem}
            listType={listType}
            setDrawerState={setDrawerState}
            typedUpdate={typedUpdate}
            onRemove={handleRemoveSubgroup}
            handleToggleOperator={handleToggleOperator}
            selectedLocationSegment={selectedLocationSegment[index] ?? audienceLocation.Any}
            handleSelectedLocationSegmentChange={segment => handleSelectedLocationSegmentChange(segment, index)}
            shouldUseRegionFirstFlow={shouldUseRegionFirstFlow}
            siblingOperatorRestrictionReason={siblingOperatorRestrictionReason}
            isExpanded={isExpanded}
            toggle={toggle}
            setExpanded={setExpanded}
          />
        );
      })}

      {errorMessage && (
        <Box sx={{ mb: 2 }} data-test="subgroup-error-message">
          <Typography
            variant="caption"
            sx={{
              color: 'error.main',
              display: 'block',
            }}
          >
            {errorMessage}
          </Typography>
        </Box>
      )}

      {isEdit && (
        <>
          <Button variant="text" color="primary" onClick={handleAddSubgroup} data-test={'subgroup-add-subgroup-button'}>
            {t('Add Subgroup')}
          </Button>

          <Tooltip
            title={t(
              'Add a segment subgroup to your segment group, to be combined (Any) or intersected (All) with other segment subgroups'
            )}
          >
            <IconButton size="large">
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
};
